import React, { useState, useEffect, lazy, Suspense } from "react";
import { Col, Navbar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import HeaderLanding, { HeaderNav } from "../Header/HeaderLanding";
import Hero from "./sections/Hero";
import BentoFeatureGrid from "./sections/BentoFeatureGrid";
import STDCFramework from "./sections/STDCFramework";
import FAQ from "./sections/FAQ";
import RepeatCTA from "./sections/RepeatCTA";

const Footer = lazy(() => import("../Footer"));
const ModalPopupContact = lazy(() => import("../ModalPopupContact"));
const Buttons = lazy(() => import("../Button/Buttons"));

import "../../styles/marketingLanding.css";

const MarketingLanding = ({ sector, style }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 0) {
        setHeaderVisible(false);
        setLastScrollY(currentScrollY);
        return;
      }
      if (currentScrollY < lastScrollY) {
        setHeaderVisible(true);
      } else {
        setHeaderVisible(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    document.documentElement.classList.add("js-loading");
    const timeout = setTimeout(() => {
      document.documentElement.classList.remove("js-loading");
    }, 1000);
    if ("fonts" in document) {
      Promise.all([
        document.fonts.load("700 1em Oxanium"),
        document.fonts.load("400 1em Rubik"),
      ]).then(() => {
        clearTimeout(timeout);
        document.documentElement.classList.remove("js-loading");
      });
    }
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <Helmet />
      <div
        className='inaqpage marketing-landing overflow-x-hidden max-w-full'
        style={style}
      >
        <HeaderLanding
          topSpace={{ md: true }}
          type='reverse-scroll'
          width='100%'
          className={`w-full bg-inaqpurple fixed transition-all duration-300 block md:hidden ${headerVisible ? "translate-y-0" : "-translate-y-full"}`}
        >
          {" "}
          <HeaderNav
            theme='light'
            expand='lg'
            bg='inaqpurple'
            containerClass='sm:!px-0'
            className='py-[0px] px-[35px] md:pr-[15px] md:pl-[10px] md:py-[12px] max-w-[1280px] mx-auto h-[70px]'
            fluid='fluid'
          >
            <Col sm={6} lg={2} xs='auto' className='me-auto ps-0'>
              <Link
                aria-label='logo home'
                className='flex items-center'
                to='/'
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Navbar.Brand className='inline-block p-0 m-0'>
                  <img
                    className='default-logo'
                    width='111'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />
                  <img
                    className='mobile-logo'
                    width='111'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />
                </Navbar.Brand>
              </Link>
            </Col>
            <Navbar.Collapse className='col-auto justify-center menu-order inaq-header-menu px-lg-0 col-lg-8'>
              <div className="font-['Oxanium'] text-white text-center w-full">
                <a href='/' className='text-white no-underline hover:text-white'>
                  {sector.header.brandLabel}
                </a>
              </div>
            </Navbar.Collapse>
            <Col xs='auto' lg={2} className='text-right pe-0'>
              <Buttons
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='!rounded-[2px] font-medium font-rubik uppercase md:hidden hover:text-inaqgold'
                themeColor={["#fff", "#fff"]}
                size='xs'
                backgroundColor='transparent'
                color='#212529'
                title={sector.header.ctaLabel}
              />
            </Col>
          </HeaderNav>
        </HeaderLanding>
        <ModalPopupContact
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        <div className='h-[70px]'></div>

        <Hero hero={sector.hero} />
        <BentoFeatureGrid bento={sector.bento} />
        <STDCFramework stdc={sector.stdc} />
        <FAQ faq={sector.faq} />
        <RepeatCTA cta={sector.cta} onOpenModal={() => setIsModalOpen(true)} />

        <Suspense fallback={<div className='h-[200px] bg-gray-200 animate-pulse'></div>}>
          <Footer
            className='inaq-footer'
            theme='light'
            logo='/assets/img/webp/inaq_logo.svg'
            width='40'
            objectFit='contain'
            loading='lazy'
          />
        </Suspense>
      </div>
      <style>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeInFromTop {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
    </>
  );
};

export default MarketingLanding;
